import React, { createContext, useContext, ReactNode, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useAppSelector, useAppDispatch } from "@/state/hooks";
import { UserProfiles } from "@/api";
import { isTokenExpired } from "@/utils/tokenUtils";
import { clearToken, clearClerkId, clearProfile } from "@/state/token/clientTokenSlice";
import { useNavigate } from "react-router-dom";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: UserProfiles | null;
    error: string | null;
    retryAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { isSignedIn, isLoaded, signOut } = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const userProfile = useAppSelector(state => state.userProfiles.userProfile);
    const userProfileStatus = useAppSelector(state => state.userProfiles.status);
    const token = useAppSelector(state => state.clientToken.token);

    // Use token refresh hook
    useTokenRefresh();

    const handleLogout = useCallback(async () => {
        // Clear Redux state
        dispatch(clearToken());
        dispatch(clearClerkId());
        dispatch(clearProfile());

        // Sign out from Clerk
        await signOut();

        // Navigate to splash
        navigate("/splash", { replace: true });
    }, [dispatch, signOut, navigate]);

    // Check token expiration periodically
    useEffect(() => {
        if (!token) return;

        // Check immediately
        if (isTokenExpired(token)) {
            console.log("Token expired, logging out");
            handleLogout();
            return;
        }

        // Check every 30 seconds
        const interval = setInterval(() => {
            if (isTokenExpired(token)) {
                console.log("Token expired during session, logging out");
                handleLogout();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [token, handleLogout]);

    // More nuanced loading state that accounts for profile loading
    const isLoading = !isLoaded || (isSignedIn && !userProfile && userProfileStatus === "loading");

    const isAuthenticated = isSignedIn && !!token && !isTokenExpired(token) && (userProfileStatus === "succeeded" || userProfileStatus === "loading");

    const retryAuth = useCallback(() => {
        // Placeholder for retry logic if needed in the future
    }, []);

    const contextValue: AuthContextType = useMemo(() => ({
        isAuthenticated: isAuthenticated ?? false,
        isLoading: isLoading ?? false,
        user: userProfile,
        error: userProfileStatus === "failed" ? "Failed to load user profile" : null,
        retryAuth
    }), [isAuthenticated, isLoading, userProfile, userProfileStatus, retryAuth]);

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
